import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  X,
  Sparkles,
  CheckCircle,
  Globe,
  Brain,
  SlidersHorizontal,
  Blocks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { aiAssistantService } from "@/services/aiAssistantService";
import { FuturisticLoader } from "@/components/ui/FuturisticLoader";

interface AIAssistantCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIAssistantCreationModal: React.FC<AIAssistantCreationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressStep, setProgressStep] = useState<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressMessages = [
    "Understanding your intent",
    "Searching the web…",
    "Creating the master prompt",
    "Adding custom inputs",
    "Selecting the best AI model",
    "Formatting assistant results",
  ];
  const { organization } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCreate = async () => {
    if (!organization?.id) {
      toast({
        title: "Error",
        description: "Organization not found",
        variant: "destructive",
      });
      return;
    }
    if (!description.trim()) {
      toast({
        title: "Add details",
        description: "Please describe the assistant you want to build.",
      });
      return;
    }

    setIsSubmitting(true);
    setProgressStep(0);
    try {
      // Simulate staged progress text updates
      if (!tickerRef.current) {
        tickerRef.current = setInterval(() => {
          setProgressStep((s) => (s + 1) % progressMessages.length);
        }, 4000);
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No active session. Please sign in again.");
      }
      // Use service abstraction for the fetch call
      const { assistant, instance } =
        await aiAssistantService.generateAssistantSetup({
          description,
          organizationId: organization.id,
          accessToken: session.access_token,
        });

      const instanceId = instance?.id;
      if (!instanceId) {
        throw new Error("Missing instance id");
      }
      toast({
        title: "AI tool created",
        description: "You can start using it now.",
      });
      onClose();
      navigate(`/ai-assistants/${instanceId}`);
    } catch (e: any) {
      toast({
        title: "Creation failed",
        description: e?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setProgressStep(0);
      if (tickerRef.current) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    }
  };

  // Ensure ticker is cleared if component unmounts or modal closes
  useEffect(() => {
    if (!isOpen && tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    return () => {
      if (tickerRef.current) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      modal={true}
    >
      <DialogPortal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-48px)] sm:w-[calc(100vw-80px)] md:w-[800px] max-w-[95vw] translate-x-[-50%] translate-y-[-50%] rounded-xl border border-gray-200 bg-white p-0 shadow-xl overflow-hidden duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 z-10"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>
          <div className="relative py-10 px-6 md:px-10 pb-16 w-full h-full flex flex-col">
            <div className="text-center max-w-2xl mx-auto mb-6">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
                <Sparkles className="h-6 w-6" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Create a custom AI tool with AI
              </h2>
              <p className="text-gray-600">
                Describe what you want. We'll generate the AI tool, configure
                inputs, and set up formatting automatically.
              </p>
            </div>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Example: A marketing AI tool that writes LinkedIn posts for real estate agents. It should ask for topic, tone, and target audience, and produce a post ready to publish."
              className="min-h-[180px] text-base"
              disabled={isSubmitting}
            />
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button
                onClick={handleCreate}
                disabled={isSubmitting}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-8 py-6 rounded-lg text-base"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Generate AI tool with AI
              </Button>
            </div>

            {isSubmitting && (
              <div className="absolute inset-0 bg-white/85 backdrop-blur-[1px] flex flex-col items-center justify-center px-6">
                <FuturisticLoader size={100} />
                <h3 className="mt-6 text-xl font-semibold">
                  Creating your AI tool...
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  This might take up to 1 min, don't close this window.
                </p>
                <div className="mt-4 text-sm text-gray-800 text-center min-h-[20px]">
                  {progressMessages[progressStep]}
                </div>
                {/* Progress bar removed per request */}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default AIAssistantCreationModal;
